import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import MD5 from 'crypto-js/md5';
import { useNavigate } from 'react-router-dom';

const ChangePasswordModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const { currentPassword, newPassword, confirmPassword } = formData;
    const formFilled = 
      currentPassword.trim() !== '' && 
      newPassword.trim() !== '' && 
      confirmPassword.trim() !== '';
    
    setIsFormValid(formFilled);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // 清除对应字段的错误信息
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    // 验证当前密码
    if (!formData.currentPassword.trim()) {
      newErrors.currentPassword = '请输入当前密码';
    }
    
    // 验证新密码
    if (!formData.newPassword.trim()) {
      newErrors.newPassword = '请输入新密码';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = '新密码长度至少为8位';
    } else {
      // 检查密码复杂度
      const hasUppercase = /[A-Z]/.test(formData.newPassword);
      const hasLowercase = /[a-z]/.test(formData.newPassword);
      const hasNumbers = /[0-9]/.test(formData.newPassword);
      const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.newPassword);
      
      const characterTypeCount = [hasUppercase, hasLowercase, hasNumbers, hasSpecialChars].filter(Boolean).length;
      
      if (characterTypeCount < 2) {
        newErrors.newPassword = '密码必须包含大写字母、小写字母、数字、特殊符号中的至少两种';
      }
    }
    
    // 验证确认密码
    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = '两次输入的密码不一致';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;
    
    setIsSubmitting(true);
    
    try {
      // 获取用户信息和认证令牌
      const userInfo = JSON.parse(localStorage.getItem('DTC_USER_INFO'));
      if (!userInfo || !userInfo.token) {
        toast.error('获取用户信息失败，请重新登录');
        onClose();
        return;
      }
      
      const authHeader = `${userInfo.code}@${userInfo.token}`;
      
      // 计算密码的MD5哈希值并转为大写
      const hashedCurrentPassword = MD5(formData.currentPassword).toString().toUpperCase();
      const hashedNewPassword = MD5(formData.newPassword).toString().toUpperCase();
      
      // 发送修改密码请求
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authHeader
        },
        body: JSON.stringify({
          current_password: hashedCurrentPassword,
          new_password: hashedNewPassword
        })
      });
      
      // 处理不同的响应状态
      if (response.status !== 200) {
        try {
          // 尝试解析为JSON
          const errorData = await response.json();
          if (errorData && errorData.message) {
            toast.error(errorData.message);
          } else {
            toast.error('服务端临时反馈了一个异常，请稍后再试');
          }
        } catch (parseError) {
          // 如果不是JSON对象
          toast.error('服务端临时反馈了一个异常，请稍后再试');
        }
        setIsSubmitting(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.success === true) {
        toast.success('密码修改成功，请重新登录');
        // 重置表单
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        // 清除用户登录信息
        localStorage.removeItem('DTC_USER_INFO');
        // 关闭弹窗
        onClose();
        // 延迟跳转到登录页，让用户能看到成功提示
        setTimeout(() => {
          navigate('/login');
        }, 2000); // 2秒后跳转
      } else {
        toast.error(data.message || '密码修改失败，请稍后重试');
      }
    } catch (error) {
      console.error('修改密码出错:', error);
      toast.error('网络错误，请稍后重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 overflow-hidden animate-fade-in-up">
        <div className="flex justify-between items-center p-5 border-b">
          <h3 className="text-lg font-medium text-gray-900">修改密码</h3>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              当前密码
            </label>
            <input
              type="password"
              name="currentPassword"
              value={formData.currentPassword}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                errors.currentPassword ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.currentPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              新密码
            </label>
            <input
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                errors.newPassword ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              确认新密码
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
            )}
          </div>

          <div className="flex items-center justify-end pt-4 space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !isFormValid}
              className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                (isSubmitting || !isFormValid) ? 'opacity-70 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? '提交中...' : '确认修改'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordModal; 